import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@heroui/react' // Import Button, Input, and Card
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSnapshot } from 'valtio'
import type { RadarMetadata } from '../../../../types'
import { EmailPrompt } from '../../components/Admin/EmailPrompt'
import { InlineSaveButton } from '../../components/common/InlineSaveButton'
import { SettingsCard } from '../../components/settings/SettingsCard' // Import SettingsCard
import { api } from '../../lib/api'
import { exportRadarData } from '../../lib/exportData'
import { state } from '../../lib/state'
import styles from './AdminRadarListPage.module.css' // Updated import path
import { logError } from '@/components/Error/Error'

type RadarItemProps = {
  radar: RadarMetadata
  editing: boolean
  onRename: (id: number, newName: string) => Promise<void>
  onDelete: (id: number) => Promise<void>
  onEdit: () => void
  onCancel: () => void
}

const RadarItem: React.FC<RadarItemProps> = ({
  radar,
  editing,
  onRename,
  onDelete,
  onEdit,
  onCancel
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [name, setName] = useState(radar.name)
  const [isSaving, setIsSaving] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const { availableRadars = [] } = useSnapshot(state)
  const isLastRadar = availableRadars?.length === 1
  const isDirty = name.trim() !== radar.name.trim()
  const navigate = useNavigate()
  useEffect(() => {
    setHasSaved(false)
    setName(radar.name)
  }, [radar.name])

  const handleSave = async () => {
    if (!name.trim()) {
      onCancel()
      return
    }
    setIsSaving(true)
    try {
      await onRename(radar.id, name)
      setHasSaved(true)
    } catch (error) {
      logError('RadarItem', 'Failed to rename radar:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setName(radar.name)
    setHasSaved(false)
    onCancel()
  }

  const handleExportAllData = async () => {
    try {
      setIsExporting(true)
      await exportRadarData()
    } catch (err) {
      logError('RadarItem', 'Error exporting data:', err)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <div className={styles.radarInfo}>
        <div className="flex flex-1 items-center gap-1">
          <Input
            autoFocus={editing}
            classNames={{
              input:
                'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
              inputWrapper: `border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 flex-1${editing ? '' : ' cursor-pointer'}`
            }}
            data-testid={`radar-name-input-${radar.id}`}
            onChange={
              editing
                ? e => {
                    setName(e.target.value)
                    setHasSaved(false)
                  }
                : undefined
            }
            onClick={editing ? undefined : onEdit}
            onKeyDown={
              editing
                ? e => {
                    if (e.key === 'Enter') handleSave()
                    else if (e.key === 'Escape') handleCancel()
                  }
                : undefined
            }
            type="text"
            value={editing ? name : radar.name}
          />
          {editing && isDirty && !hasSaved && (
            <InlineSaveButton
              ariaLabel="Save radar name"
              isLoading={isSaving}
              onClick={handleSave}
              title="Save"
            />
          )}
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.viewButton} ml-4 whitespace-nowrap`}
            onClick={() => {
              // Set the current radar and navigate to items
              state.currentRadarId = radar.id
              navigate('/admin/items')
            }}
            type="button"
          >
            Manage Radar
          </button>
          {!isLastRadar && (
            <Button
              className="h-8 rounded-[3px] px-3"
              color="danger"
              disableRipple
              onPress={
                editing
                  ? () => onDelete(radar.id)
                  : () => setShowDeleteDialog(true)
              }
              size="sm"
              variant="light"
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      {!editing && (
        <Modal
          aria-labelledby="delete-modal-title"
          classNames={{
            base: 'heroui-modal-content',
            header: 'heroui-modal-header',
            body: 'heroui-modal-body',
            footer: 'heroui-modal-footer'
          }}
          data-testid="delete-modal"
          isOpen={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
        >
          <ModalContent>
            <ModalHeader>
              <h3
                className="font-medium text-[var(--ds-text)] text-lg"
                data-testid="delete-modal-title"
                id="delete-modal-title"
              >
                Delete Radar
              </h3>
            </ModalHeader>
            <ModalBody>
              <p className="mb-4">
                Are you sure you want to delete the "{radar.name}" radar? This
                action cannot be undone.
              </p>
              <p className="text-[var(--ds-text-subtle)] text-sm">
                Note: Before deletion, we'll automatically export your radar
                data as a JSON file, which can be re-imported if needed.
              </p>
              {isLastRadar && (
                <p className="mt-2 text-[var(--ds-text-danger)] text-sm">
                  You cannot delete the last remaining radar. At least one radar
                  must exist.
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                className="h-8 rounded-[3px] border border-[var(--ds-border)] bg-transparent px-3 text-[var(--ds-text)] hover:bg-transparent"
                disableRipple
                onPress={() => setShowDeleteDialog(false)}
                size="sm"
                variant="bordered"
              >
                Cancel
              </Button>
              <Button
                className="h-8 rounded-[3px] px-3"
                color="danger"
                disableRipple
                isDisabled={isExporting || isLastRadar}
                onPress={async () => {
                  await handleExportAllData()
                  await onDelete(radar.id)
                  setShowDeleteDialog(false)
                }}
                size="sm"
                variant="solid"
              >
                {isExporting ? 'Exporting...' : 'Delete'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

export const RadarListPage = () => {
  const { availableRadars = [] } = useSnapshot(state)
  const [newRadarName, setNewRadarName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [radarLimitCheck, setRadarLimitCheck] = useState<{
    canCreateRadar: boolean
    currentCount: number
    maxAllowed: number
  } | null>(null)

  // Check radar limits on component mount
  useEffect(() => {
    const checkLimits = async () => {
      try {
        const limitCheck = await api.checkRadarLimit()
        setRadarLimitCheck(limitCheck)
      } catch (err) {
        logError('RadarListPage', 'Error checking radar limits:', err)
      }
    }

    checkLimits()
  }, [])

  const handleCreateRadar = async () => {
    if (!newRadarName.trim()) {
      setError('Radar name cannot be empty')
      return
    }

    // Check radar limits before creating
    if (radarLimitCheck && !radarLimitCheck.canCreateRadar) {
      setError(
        `Free tier is limited to ${radarLimitCheck.maxAllowed} radar${radarLimitCheck.maxAllowed === 1 ? '' : 's'}. Please upgrade to create more radars.`
      )
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      // Create the new radar
      await api.createRadar(newRadarName.trim())
      setNewRadarName('')

      // Refresh radar limit check after creating
      const limitCheck = await api.checkRadarLimit()
      setRadarLimitCheck(limitCheck)
    } catch (err) {
      logError('RadarListPage', 'Error creating radar:', err)
      const errorMessage =
        (err as Error)?.message || 'Failed to create radar. Please try again.'
      setError(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  const handleRenameRadar = async (radarId: number, newName: string) => {
    const trimmedName = newName.trim()
    if (!trimmedName) {
      setError('Radar name cannot be empty')
      return
    }

    const originalRadar = availableRadars?.find(
      r => Number(r.id) === Number(radarId)
    )
    if (!originalRadar) {
      setError('Radar not found')
      setEditingId(null)
      return
    }

    if (originalRadar.name === trimmedName) {
      setEditingId(null)
      return
    }

    setError(null)

    try {
      const updatedRadar = await api.updateRadar(radarId, trimmedName)

      // Find the index of the radar to update
      const indexToUpdate = (state.availableRadars ?? []).findIndex(
        r => Number(r.id) === Number(radarId)
      )
      if (indexToUpdate !== -1 && state.availableRadars) {
        // Mutate the radar object in place for Valtio reactivity
        Object.assign(state.availableRadars[indexToUpdate], updatedRadar)
      }

      setEditingId(null)
    } catch (err) {
      // Use the same error handler pattern as the rest of the project
      const message =
        (err as Error)?.message || 'Failed to rename radar. Please try again.'
      logError('RadarListPage', 'Error renaming radar:', err)
      setError(message)
    }
  }

  const handleDeleteRadar = async (radarId: number) => {
    setError(null)

    try {
      // Delete all items for this radar first
      await api.deleteAllItemsForRadar(radarId)

      // Now delete the radar itself
      const deleteResult = await api.deleteRadar(radarId)

      // Check if deletion was successful
      if (!deleteResult.success) {
        setError(deleteResult.error || 'Failed to delete radar')
        return
      }

      // Update the local state by removing the deleted radar
      const idx = (state.availableRadars ?? []).findIndex(
        r => Number(r.id) === Number(radarId)
      )
      if (idx !== -1 && state.availableRadars) {
        state.availableRadars.splice(idx as number, 1)
      }

      // Refresh radar limit check after deleting
      const limitCheck = await api.checkRadarLimit()
      setRadarLimitCheck(limitCheck)
    } catch (err) {
      logError('RadarListPage', 'Error deleting radar and its items:', err)
      setError('Failed to delete radar and its items. Please try again.')
    }
  }

  return (
    <div className="mt-6 space-y-6 pb-6">
      {error && (
        <div className="fixed right-4 bottom-4 z-50 rounded-sm bg-[var(--ds-background-danger)] p-3 text-[var(--ds-text-danger)] text-sm shadow-lg">
          {error}
        </div>
      )}
      <EmailPrompt />
      <SettingsCard>
        <div className={styles.radarList}>
          <h2 className="text-[var(--ds-text)]">Manage Radars</h2>
          {(!Array.isArray(availableRadars) ||
            availableRadars?.length === 0) && (
            <div className="rounded border border-[var(--ds-border)] bg-[var(--ds-surface-secondary)] p-3 text-[var(--ds-text)] text-sm">
              You don't have any radars yet. Get started by creating one below!
            </div>
          )}
          <ul className={styles.list}>
            {availableRadars?.map(radar => {
              return radar?.id ? (
                <li className={styles.radarItem} key={radar.id}>
                  <RadarItem
                    editing={editingId === radar.id}
                    onCancel={() => setEditingId(null)}
                    onDelete={handleDeleteRadar}
                    onEdit={() => setEditingId(radar.id)}
                    onRename={handleRenameRadar}
                    radar={{ ...radar } as RadarMetadata}
                  />
                </li>
              ) : null
            })}
          </ul>
        </div>
      </SettingsCard>
      <SettingsCard>
        <div className="flex flex-col gap-4">
          {radarLimitCheck && !radarLimitCheck.canCreateRadar && (
            <div className="rounded-md border border-orange-300 p-4 text-[var(--ds-text)] text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-orange-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      fillRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">
                    You've reached the free tier limit of{' '}
                    {radarLimitCheck.maxAllowed} radar
                    {radarLimitCheck.maxAllowed === 1 ? '' : 's'} (
                    {radarLimitCheck.currentCount}/{radarLimitCheck.maxAllowed}
                    ).
                  </span>
                </div>
                <Button
                  as="a"
                  className="ml-4 rounded-md bg-orange-600 px-3 py-1 font-medium text-sm text-white hover:bg-orange-700"
                  href="/admin/billing"
                  size="sm"
                  variant="solid"
                >
                  Upgrade
                </Button>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <label
              className="font-medium text-[var(--ds-text)] text-sm"
              htmlFor="new-radar-name"
            >
              Create New Radar
            </label>
            <div className="flex items-center gap-2">
              <Input
                classNames={{
                  input:
                    'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none caret-[var(--ds-text)]',
                  inputWrapper:
                    'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 flex-1'
                }}
                disabled={
                  Boolean(isCreating) ||
                  Boolean(radarLimitCheck && !radarLimitCheck.canCreateRadar)
                }
                id="new-radar-name"
                onChange={e => setNewRadarName(e.target.value)}
                placeholder="Enter radar name"
                type="text"
                value={newRadarName}
              />
              <Button
                className="h-10 rounded-[3px] bg-primary px-3 font-bold text-white hover:bg-hover"
                disableRipple
                isDisabled={
                  Boolean(isCreating) ||
                  !newRadarName.trim() ||
                  Boolean(radarLimitCheck && !radarLimitCheck.canCreateRadar)
                }
                onPress={handleCreateRadar}
                size="sm"
                variant="solid"
              >
                {isCreating ? 'Creating...' : 'Create Radar'}
              </Button>
            </div>
          </div>
        </div>
      </SettingsCard>
    </div>
  )
}

export default RadarListPage
